import { apiClient } from '../client';
import type { 
  Space, 
  SpaceMember, 
  CreateSpaceDto, 
  UpdateSpaceDto, 
  SpacesFilters, 
  SpacesResponse,
  SpaceRequest,
  ApiResponse,
  SpaceEvent 
} from '../../types';

class SpacesService {
  // Space CRUD operations
  async getSpaces(filters: SpacesFilters = {}): Promise<SpacesResponse> {
    const params = new URLSearchParams();
    
    if (filters.campus_id) params.append('campus_id', filters.campus_id);
    if (filters.owner_id) params.append('owner_id', filters.owner_id);
    if (filters.search) params.append('search', filters.search);
    if (filters.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters.is_deleted !== undefined) params.append('is_deleted', String(filters.is_deleted));
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.sort) params.append('sort', filters.sort);
    if (filters.order) params.append('order', filters.order);
    
    const response = await apiClient.get(`/spaces?${params.toString()}`);
    return response.data;
  }

  async getSpace(spaceId: string): Promise<Space> {
    const response = await apiClient.get(`/spaces/${spaceId}`);
    return response.data;
  }

  async createSpace(data: CreateSpaceDto): Promise<ApiResponse<Space>> {
    const response = await apiClient.post('/spaces', data);
    return response.data;
  }

  async updateSpace(spaceId: string, data: UpdateSpaceDto): Promise<ApiResponse<Space>> {
    const response = await apiClient.patch(`/spaces/${spaceId}`, data);
    return response.data;
  }

  async deleteSpace(spaceId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.delete(`/spaces/${spaceId}`);
    return response.data;
  }

  async restoreSpace(spaceId: string): Promise<ApiResponse<Space>> {
    const response = await apiClient.post(`/spaces/${spaceId}/restore`);
    return response.data;
  }

  // Membership operations
  async requestToJoin(spaceId: string): Promise<ApiResponse<SpaceMember>> {
    const response = await apiClient.post(`/spaces/${spaceId}/join`);
    return response.data;
  }

  async approveRequest(spaceId: string, userId: string): Promise<ApiResponse<SpaceMember>> {
    const response = await apiClient.post(`/spaces/${spaceId}/requests/${userId}/approve`);
    return response.data;
  }

  async rejectRequest(spaceId: string, userId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.post(`/spaces/${spaceId}/requests/${userId}/reject`);
    return response.data;
  }

  async getRequests(spaceId: string): Promise<{ items: SpaceRequest[]; total: number; page: number; limit: number }> {
    const response = await apiClient.get(`/spaces/${spaceId}/requests`);
    return response.data;
  }

  async getMembers(spaceId: string, page = 1, limit = 20): Promise<{ items: SpaceMember[]; total: number; page: number; limit: number }> {
    const response = await apiClient.get(`/spaces/${spaceId}/members?page=${page}&limit=${limit}`);
    return response.data;
  }

  async removeMember(spaceId: string, userId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.delete(`/spaces/${spaceId}/members/${userId}`);
    return response.data;
  }

  async leaveSpace(spaceId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.post(`/spaces/${spaceId}/leave`);
    return response.data;
  }

  // Space events
  async getSpaceEvents(spaceId: string, page = 1, limit = 20): Promise<ApiResponse<{
    events: SpaceEvent[];
    total: number;
    page: number;
    pages: number;
  }>> {
    const response = await apiClient.get(`/spaces/${spaceId}/events?page=${page}&limit=${limit}`);
    return response.data;
  }

  async createSpaceEvent(spaceId: string, eventData: Partial<SpaceEvent>): Promise<ApiResponse<SpaceEvent>> {
    const response = await apiClient.post(`/spaces/${spaceId}/events`, eventData);
    return response.data;
  }

  // Admin operations
  async adminUpdateSpace(spaceId: string, data: UpdateSpaceDto): Promise<ApiResponse<Space>> {
    const response = await apiClient.patch(`/admin/spaces/${spaceId}`, data);
    return response.data;
  }

  async transferOwnership(spaceId: string, newOwnerId: string): Promise<ApiResponse<Space>> {
    const response = await apiClient.post(`/admin/spaces/${spaceId}/owner`, { new_owner_id: newOwnerId });
    return response.data;
  }

  async forceApprove(spaceId: string, userId: string): Promise<ApiResponse<SpaceMember>> {
    const response = await apiClient.post(`/admin/spaces/${spaceId}/requests/${userId}/approve`);
    return response.data;
  }

  async forceRemove(spaceId: string, userId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.delete(`/admin/spaces/${spaceId}/members/${userId}`);
    return response.data;
  }

  // Image upload
  async uploadSpaceImage(spaceId: string, file: File): Promise<ApiResponse<{ image_url: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await apiClient.post(`/spaces/${spaceId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // User's spaces
  async getUserSpaces(userId: string): Promise<ApiResponse<Space[]>> {
    const response = await apiClient.get(`/users/${userId}/spaces`);
    return response.data;
  }

  async getMySpaces(): Promise<ApiResponse<Space[]>> {
    const response = await apiClient.get('/me/spaces');
    return response.data;
  }
}

export default new SpacesService();